/**
 * ============================================================================
 * useGestures
 * ============================================================================
 * 
 * 마우스 휠 및 터치 제스처 처리
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 1. PC: 마우스 휠로 페이지 이동 (2번 이상 움직여야 동작)
 * 2. 모바일: 터치 제스처로 페이지 이동
 * 
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 사용 예시
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * useGestures({
 *   containerRef,
 *   pageData,
 *   handlePrevPage,
 *   handleNextPage,
 *   isModalOpen,
 *   isPageNumberExpanded,
 *   isRecordingModalOpen,
 *   showLearningRecords,
 *   showAIPanel,
 * });
 * 
 * ============================================================================
 */

import { useEffect, useRef } from 'react';

interface UseGesturesParams {
  containerRef: React.RefObject<HTMLDivElement | null>;
  pageData: any;
  handlePrevPage: () => void;
  handleNextPage: () => void;
  isModalOpen: boolean;
  isPageNumberExpanded: boolean;
  isRecordingModalOpen: boolean;
  showLearningRecords: boolean;
  showAIPanel: boolean;
}

export const useGestures = ({
  containerRef,
  pageData,
  handlePrevPage,
  handleNextPage,
  isModalOpen,
  isPageNumberExpanded,
  isRecordingModalOpen,
  showLearningRecords,
  showAIPanel,
}: UseGesturesParams) => {
  // 마우스 휠 카운터 (PC용)
  const wheelCounterRef = useRef<number>(0);
  const wheelTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastPageChangeTimeRef = useRef<number>(0);
  const WHEEL_COOLDOWN_MS = 400;
  
  // 터치 제스처 상태 (모바일용)
  const touchStartYRef = useRef<number | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const touchStartTimeRef = useRef<number | null>(null);
  const touchStartTargetRef = useRef<HTMLElement | null>(null);
  const isScrollingRef = useRef<boolean>(false);
  const isTextSelectionRef = useRef<boolean>(false);
  
  // PC: 마우스 휠로 페이지 이동 (2번 이상 움직여야 동작)
  useEffect(() => {
    // 모바일 감지: 터치 이벤트가 있는지 확인
    const isMobile = 'ontouchstart' in window || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
    
    // 모바일에서는 마우스 휠 이벤트 비활성화
    if (isMobile) {
      return;
    }
    
    // pageData가 없으면 이벤트 리스너 등록하지 않음
    if (!pageData) {
      return;
    }
    
    const handleWheel = (e: WheelEvent) => {
      // Container가 마운트되어 있는지 확인
      const container = containerRef.current;
      if (!container) {
        return;
      }
      
      // Container 내부에서 발생한 이벤트인지 확인
      const target = e.target as HTMLElement;
      if (!container.contains(target)) {
        return;
      }

      // PDF 뷰어 내부에서는 PDF 자체 휠 핸들러에 위임
      if (target.closest('[data-pdf-viewer]')) {
        return;
      }
      
      // 패널이 열려있으면 패널 내부 스크롤 허용 (페이지 이동 비활성화)
      if (
        target.closest('[data-learning-panel]') ||
        target.closest('[data-ai-panel]') ||
        showLearningRecords ||
        showAIPanel
      ) {
        return; // 패널 내부에서는 기본 스크롤 동작 허용
      }
      
      // 모달이나 입력 필드가 열려있으면 무시
      if (
        target.closest('[data-modal]') ||
        target.closest('input') ||
        target.closest('textarea') ||
        isModalOpen ||
        isPageNumberExpanded ||
        isRecordingModalOpen
      ) {
        return;
      }
      
      // 기본 스크롤 동작 방지
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // 쿨다운: 최근 페이지 변경 후 일정 시간 내에는 한 페이지만 넘어가도록 무시
      const now = Date.now();
      if (now - lastPageChangeTimeRef.current < WHEEL_COOLDOWN_MS) {
        return;
      }
      
      // 휠 카운터 증가
      wheelCounterRef.current += 1;
      
      // 기존 타이머 클리어
      if (wheelTimeoutRef.current) {
        clearTimeout(wheelTimeoutRef.current);
      }
      
      // 2번 이상 움직였을 때만 페이지 이동
      if (wheelCounterRef.current >= 2) {
        const deltaY = e.deltaY;
        
        if (deltaY > 0) {
          // 아래로 스크롤 -> 다음 페이지
          handleNextPage();
        } else if (deltaY < 0) {
          // 위로 스크롤 -> 이전 페이지
          handlePrevPage();
        }
        
        // 페이지 이동 후 카운터 리셋 및 쿨다운 시작
        wheelCounterRef.current = 0;
        lastPageChangeTimeRef.current = now;
      }
      
      // 일정 시간(500ms) 내에 움직임이 없으면 카운터 리셋
      wheelTimeoutRef.current = setTimeout(() => {
        wheelCounterRef.current = 0;
      }, 500);
    };
    
    // document 레벨에서 이벤트 캡처 (가장 확실하게)
    document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    
    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true } as EventListenerOptions);
      if (wheelTimeoutRef.current) {
        clearTimeout(wheelTimeoutRef.current);
      }
    };
  }, [containerRef, pageData, handlePrevPage, handleNextPage, isModalOpen, isPageNumberExpanded, isRecordingModalOpen, showLearningRecords, showAIPanel]);
  
  // 모바일: 터치 제스처로 페이지 이동
  useEffect(() => {
    // 모바일 감지: 터치 이벤트가 있는지 확인
    const isMobile = 'ontouchstart' in window || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
    
    // 모바일이 아니면 터치 이벤트 비활성화
    if (!isMobile) {
      return;
    }
    
    // pageData가 없으면 이벤트 리스너 등록하지 않음
    if (!pageData) {
      return;
    }
    
    const handleTouchStart = (e: TouchEvent) => {
      // Container가 마운트되어 있는지 확인
      const container = containerRef.current;
      if (!container) {
        return;
      }
      
      // Container 내부에서 발생한 이벤트인지 확인
      const target = e.target as HTMLElement;
      if (!container.contains(target)) {
        return;
      }
      
      // 패널 내부 터치는 패널 자체 스크롤에 위임
      if (
        target.closest('[data-learning-panel]') ||
        target.closest('[data-ai-panel]')
      ) {
        return;
      }
      
      // 모달이나 입력 필드가 열려있으면 무시
      if (
        target.closest('[data-modal]') ||
        target.closest('input') ||
        target.closest('textarea') ||
        isModalOpen ||
        isPageNumberExpanded ||
        isRecordingModalOpen
      ) {
        return;
      }
      
      const touch = e.touches[0];
      if (touch) {
        touchStartYRef.current = touch.clientY;
        touchStartXRef.current = touch.clientX;
        touchStartTimeRef.current = Date.now();
        touchStartTargetRef.current = target;
        isScrollingRef.current = false;
        isTextSelectionRef.current = false;
      }
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartYRef.current === null || touchStartTimeRef.current === null) {
        return;
      }
      
      // 텍스트 선택이 진행 중이면 브라우저 기본 동작 허용
      if (isTextSelectionRef.current) {
        return;
      }
      
      const container = containerRef.current;
      if (!container) {
        return;
      }
      
      const target = e.target as HTMLElement;
      if (!container.contains(target)) {
        return;
      }
      
      const touch = e.touches[0];
      if (!touch) {
        return;
      }
      
      const currentY = touch.clientY;
      const currentX = touch.clientX;
      const deltaY = currentY - touchStartYRef.current;
      const deltaX = currentX - (touchStartXRef.current ?? currentX);
      
      // 텍스트 선택 감지: 수평 이동이 수직보다 크거나, 텍스트 콘텐츠 위에서 선택이 시작된 경우
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        isTextSelectionRef.current = true;
        return;
      }
      
      // 수평 이동이 우세하면 텍스트 선택으로 간주하고 브라우저 기본 동작 허용
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        isTextSelectionRef.current = true;
        return;
      }
      
      // 수직 이동이 충분하면 스와이프로 간주
      if (Math.abs(deltaY) > 10) {
        isScrollingRef.current = true;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };
    
    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartYRef.current === null || touchStartTimeRef.current === null) {
        return;
      }
      
      // 텍스트 선택이 진행됐으면 페이지 이동 없이 리셋
      if (isTextSelectionRef.current) {
        touchStartYRef.current = null;
        touchStartXRef.current = null;
        touchStartTimeRef.current = null;
        touchStartTargetRef.current = null;
        isScrollingRef.current = false;
        isTextSelectionRef.current = false;
        return;
      }
      
      const touch = e.changedTouches[0];
      if (!touch) {
        touchStartYRef.current = null;
        touchStartXRef.current = null;
        touchStartTimeRef.current = null;
        touchStartTargetRef.current = null;
        isScrollingRef.current = false;
        return;
      }
      
      const container = containerRef.current;
      if (!container) {
        touchStartYRef.current = null;
        touchStartXRef.current = null;
        touchStartTimeRef.current = null;
        touchStartTargetRef.current = null;
        isScrollingRef.current = false;
        return;
      }
      
      // 터치가 시작된 요소와 같은 요소에서 끝났는지 확인
      const target = e.target as HTMLElement;
      if (touchStartTargetRef.current && !container.contains(target)) {
        touchStartYRef.current = null;
        touchStartXRef.current = null;
        touchStartTimeRef.current = null;
        touchStartTargetRef.current = null;
        isScrollingRef.current = false;
        return;
      }
      
      const endY = touch.clientY;
      const startY = touchStartYRef.current;
      const deltaY = endY - startY;
      const touchDuration = Date.now() - touchStartTimeRef.current;
      
      // 최소 이동 거리 (30px)와 최대 시간(800ms) 체크
      const minDistance = 30;
      const maxDuration = 800;
      
      // 스크롤이 발생했고 최소 이동 거리와 시간 조건을 만족하면 페이지 이동
      if (isScrollingRef.current && Math.abs(deltaY) >= minDistance && touchDuration <= maxDuration) {
        if (deltaY < 0) {
          // 위로 스와이프 (콘텐츠를 위로 올림) -> 다음 페이지
          handleNextPage();
        } else {
          // 아래로 스와이프 (콘텐츠를 아래로 내림) -> 이전 페이지
          handlePrevPage();
        }
      }
      
      // 상태 리셋
      touchStartYRef.current = null;
      touchStartXRef.current = null;
      touchStartTimeRef.current = null;
      touchStartTargetRef.current = null;
      isScrollingRef.current = false;
      isTextSelectionRef.current = false;
    };
    
    const handleTouchCancel = () => {
      // 터치가 취소되면 상태 리셋
      touchStartYRef.current = null;
      touchStartXRef.current = null;
      touchStartTimeRef.current = null;
      touchStartTargetRef.current = null;
      isScrollingRef.current = false;
      isTextSelectionRef.current = false;
    };
    
    // document 레벨에서 이벤트 캡처 (가장 확실하게)
    document.addEventListener('touchstart', handleTouchStart, { passive: false, capture: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: false, capture: true });
    document.addEventListener('touchcancel', handleTouchCancel, { passive: false, capture: true });
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart, { capture: true } as EventListenerOptions);
      document.removeEventListener('touchmove', handleTouchMove, { capture: true } as EventListenerOptions);
      document.removeEventListener('touchend', handleTouchEnd, { capture: true } as EventListenerOptions);
      document.removeEventListener('touchcancel', handleTouchCancel, { capture: true } as EventListenerOptions);
    };
  }, [containerRef, pageData, handlePrevPage, handleNextPage, isModalOpen, isPageNumberExpanded, isRecordingModalOpen, showLearningRecords, showAIPanel]);
};
